import { createReducer, createActions } from 'reduxsauce'
import Immutable, { ImmutableObject } from 'seamless-immutable'
import { WellnessCenterState } from '../Store/WellnessCenter/types'
/* ------------- Types and Action Creators ------------- */

type ImmutableWellnessCenterState = ImmutableObject<WellnessCenterState>

class ActiveLeaderboardItem {
  user: unknown
  team: unknown

  constructor(user?: unknown, team?: unknown) {
    this.user = user || null
    this.team = team || null
  }
}

export const Models = {
  ActiveLeaderboardItem,
}

const actionDefs = {
  getMyChallenges: [],
  getMyChallengesSuccess: ['data'],
  getMyChallengesFailure: [],

  getNewChallenges: [],
  getNewChallengesSuccess: ['data'],
  getNewChallengesFailure: [],

  getCompletedChallenges: [],
  getCompletedChallengesSuccess: ['data'],
  getCompletedChallengesFailure: [],

  getChallenge: ['challengeId', 'challengeType'],
  getChallengeSuccess: [
    'challenge',
    'inChallenge',
    'userHasTracker',
    'userHasNoCompatibleTrackers',
    'userChallengeGoal',
  ],
  getChallengeFailure: [],
  resetChallenge: [],

  joinIndividualChallenge: ['challengeId'],
  joinIndividualChallengeSuccess: ['inChallenge'],
  joinIndividualChallengeFailure: [],

  joinTeamChallenge: ['challengeId', 'teamId'],
  joinTeamChallengeSuccess: ['inChallenge'],
  joinTeamChallengeFailure: [],

  getLeaderboard: ['challengeId', 'teamId'],
  getLeaderboardSuccess: ['participants', 'additionalParticipants', 'activeLeaderboardItem'],
  getLeaderboardFailure: [],

  getTeamParticipants: ['challengeId', 'teamId'],
  getTeamParticipantsSuccess: ['participants', 'additionalParticipants'],
  getTeamParticipantsFailure: [],

  leaveSelectedChallenge: ['challengeId'],
  leaveSelectedChallengeSuccess: ['participants'],
  leaveSelectedChallengeFailure: [],

  // getTrackers Legacy
  getTrackers: [],
  getTrackersSuccess: ['trackers'],
  getTrackersFailure: [],

  connectTracker: ['trackerName', 'id', 'callback'],
  disconnectTracker: ['trackerName', 'callback'],

  modifyingTrackers: ['trackers'],

  addMetricsTracker: [
    'trackerName',
    'trackerActivities',
    'fromDate',
    'toDate',
    'syncDate',
    'callback',
  ],

  syncAllSamplesForTrackers: [
    'trackersWithSyncOptions',
    'needsSyncTrackerStatus',
    'lastSyncTimeThreshold',
    'callback',
  ],

  healthKitSyncRequest: ['trigger'],

  setAnyTrackerLastConnectedTime: ['anyTrackerLastConnectedTime'],
} as const satisfies Record<string, string[]>

type TActions = typeof actionDefs
type TPayloads = {}
const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const WellnessCenterTypes = Types
export default Creators

export const INITIAL_STATE = Immutable<WellnessCenterState>({
  loading: true,
  retry: false,
  myChallenges: {
    loading: true,
    retry: false,
    data: undefined,
  },
  newChallenges: {
    loading: true,
    retry: false,
    data: undefined,
  },
  completedChallenges: {
    loading: true,
    retry: false,
    data: undefined,
  },
  selectedChallenge: {
    loading: true,
    retry: false,
    joining: false,
    leaving: false,
    inChallenge: false,
    userHasTracker: false,
    userHasNoCompatibleTrackers: false,
    userChallengeGoal: 0,
    participants: [],
    additionalParticipants: [],
    activeLeaderboardItem: new Models.ActiveLeaderboardItem(),
    challenge: {},
    loadingLeaderboard: true,
    retryLeaderboard: false,
  },
  selectedTeam: {
    loading: true,
    retry: false,
    participants: [],
    additionalParticipants: [],
  },
  trackers: {
    loading: true,
    retry: false,
    modifying: [],
    trackers: {
      user: [],
      supported: [],
      comingSoon: [],
    },
    anyTrackerLastConnectedTime: '',
  },
})

/* ------------- Reducers ------------- */

export const setAnyTrackerLastConnectedTime = (
  state: ImmutableWellnessCenterState,
  {
    anyTrackerLastConnectedTime,
  }: {
    anyTrackerLastConnectedTime: WellnessCenterState['trackers']['anyTrackerLastConnectedTime']
  }
): ImmutableWellnessCenterState =>
  state.merge(
    {
      trackers: {
        anyTrackerLastConnectedTime: anyTrackerLastConnectedTime,
      },
    },
    { deep: true }
  )

export const getMyChallenges = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge(
    {
      myChallenges: {
        loading: true,
        retry: false,
      },
    },
    { deep: true }
  )

export const getMyChallengesSuccess = (
  state: ImmutableWellnessCenterState,
  { data }: { data: WellnessCenterState['myChallenges']['data'] }
): ImmutableWellnessCenterState =>
  state.merge({
    myChallenges: {
      loading: false,
      retry: false,
      data,
    },
  })

export const getMyChallengesFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    myChallenges: {
      loading: false,
      retry: true,
    },
  })

export const getNewChallenges = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge(
    {
      newChallenges: {
        loading: true,
        retry: false,
      },
    },
    { deep: true }
  )

export const getNewChallengesSuccess = (
  state: ImmutableWellnessCenterState,
  { data }: { data: WellnessCenterState['newChallenges']['data'] }
): ImmutableWellnessCenterState =>
  state.merge({
    newChallenges: {
      loading: false,
      retry: false,
      data,
    },
  })

export const getNewChallengesFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    newChallenges: {
      loading: false,
      retry: true,
    },
  })

export const getCompletedChallenges = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge(
    {
      completedChallenges: {
        loading: true,
        retry: false,
      },
    },
    { deep: true }
  )

export const getCompletedChallengesSuccess = (
  state: ImmutableWellnessCenterState,
  { data }: { data: WellnessCenterState['completedChallenges']['data'] }
): ImmutableWellnessCenterState =>
  state.merge({
    completedChallenges: {
      loading: false,
      retry: false,
      data,
    },
  })

export const getCompletedChallengesFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    completedChallenges: {
      loading: false,
      retry: true,
    },
  })

export const getChallenge = (state: ImmutableWellnessCenterState): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: true,
      retry: false,
      participants: [],
      challenge: {},
      activeLeaderboardItem: new Models.ActiveLeaderboardItem(),
    },
  })

export const getChallengeSuccess = (
  state: ImmutableWellnessCenterState,
  {
    challenge,
    inChallenge,
    userHasTracker,
    userHasNoCompatibleTrackers,
    userChallengeGoal,
  }: {
    challenge: WellnessCenterState['selectedChallenge']['challenge']
    inChallenge: boolean
    userHasTracker: boolean
    userHasNoCompatibleTrackers: boolean
    userChallengeGoal: number
  }
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: false,
      retry: false,
      inChallenge,
      userHasTracker,
      userHasNoCompatibleTrackers,
      userChallengeGoal,
      challenge,
    },
  })

export const getChallengeFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: false,
      retry: true,
    },
  })

export const resetChallenge = (state: ImmutableWellnessCenterState): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      loading: true,
      retry: false,
      joining: false,
      leaving: false,
      inChallenge: false,
      userHasTracker: false,
      userHasNoCompatibleTrackers: false,
      userChallengeGoal: 0,
      participants: [],
      activeLeaderboardItem: new Models.ActiveLeaderboardItem(),
      challenge: {},
      loadingLeaderboard: true,
      retryLeaderboard: false,
    },
  })

export const joinIndividualChallenge = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: true,
      retry: false,
    },
  })

export const joinIndividualChallengeSuccess = (
  state: ImmutableWellnessCenterState,
  { inChallenge }: { inChallenge: boolean }
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: false,
      retry: false,
      inChallenge,
    },
  })

export const joinIndividualChallengeFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: false,
      retry: true,
    },
  })

export const joinTeamChallenge = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: true,
      retry: false,
    },
  })

export const joinTeamChallengeSuccess = (
  state: ImmutableWellnessCenterState,
  { inChallenge }: { inChallenge: boolean }
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: false,
      retry: false,
      inChallenge,
    },
  })

export const joinTeamChallengeFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      joining: false,
      retry: true,
    },
  })

export const getLeaderboard = (state: ImmutableWellnessCenterState): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loadingLeaderboard: true,
      retryLeaderboard: false,
      participants: [],
    },
  })

export const getLeaderboardSuccess = (
  state: ImmutableWellnessCenterState,
  {
    participants,
    additionalParticipants,
    activeLeaderboardItem,
  }: {
    participants: WellnessCenterState['selectedChallenge']['participants']
    additionalParticipants: WellnessCenterState['selectedChallenge']['additionalParticipants']
    activeLeaderboardItem: WellnessCenterState['selectedChallenge']['activeLeaderboardItem']
  }
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loadingLeaderboard: false,
      retryLeaderboard: false,
      participants,
      additionalParticipants,
      activeLeaderboardItem,
    },
  })

export const getLeaderboardFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loadingLeaderboard: false,
      retryLeaderboard: true,
    },
  })

export const getTeamParticipants = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedTeam: {
      ...state.selectedTeam,
      loading: true,
      retry: false,
      participants: [],
      additionalParticipants: [],
    },
  })

export const getTeamParticipantsSuccess = (
  state: ImmutableWellnessCenterState,
  {
    participants,
    additionalParticipants,
  }: {
    participants: WellnessCenterState['selectedTeam']['participants']
    additionalParticipants: WellnessCenterState['selectedTeam']['additionalParticipants']
  }
): ImmutableWellnessCenterState =>
  state.merge({
    selectedTeam: {
      ...state.selectedTeam,
      loading: false,
      retry: false,
      participants,
      additionalParticipants,
    },
  })

export const getTeamParticipantsFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedTeam: {
      ...state.selectedTeam,
      loading: false,
      retry: true,
    },
  })

export const leaveSelectedChallenge = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: true,
      leaving: true,
      retry: false,
    },
  })

export const leaveSelectedChallengeSuccess = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: false,
      leaving: false,
      retry: false,
    },
  })

export const leaveSelectedChallengeFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    selectedChallenge: {
      ...state.selectedChallenge,
      loading: false,
      leaving: false,
      retry: true,
    },
  })

// getTrackers Legacy
export const getTrackers = (state: ImmutableWellnessCenterState): ImmutableWellnessCenterState =>
  state.merge({
    trackers: {
      ...state.trackers,
      loading: true,
      retry: false,
      modifying: [],
    },
  })

export const getTrackersSuccess = (
  state: ImmutableWellnessCenterState,
  {
    trackers,
  }: {
    trackers: WellnessCenterState['trackers']['trackers']
  }
): ImmutableWellnessCenterState =>
  state.merge({
    trackers: {
      ...state.trackers,
      loading: false,
      retry: false,
      modifying: [],
      trackers,
    },
  })

export const getTrackersFailure = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState =>
  state.merge({
    trackers: {
      ...state.trackers,
      loading: false,
      retry: true,
      modifying: [],
    },
  })

export const connectTracker = (state: ImmutableWellnessCenterState): ImmutableWellnessCenterState =>
  state

export const disconnectTracker = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState => state

export const modifyingTrackers = (
  state: ImmutableWellnessCenterState,
  { trackers }: { trackers: WellnessCenterState['trackers']['modifying'] }
): ImmutableWellnessCenterState =>
  state.merge({
    trackers: {
      ...state.trackers.asMutable(),
      modifying: trackers,
    },
  })

export const addMetricsTracker = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState => state

export const syncAllSamplesForTrackers = (
  state: ImmutableWellnessCenterState
): ImmutableWellnessCenterState => state

/* ------------- Hookup Reducers To Types ------------- */

type TState = ImmutableWellnessCenterState
export const reducer = createReducer<TPayloads, TActions, TState>(INITIAL_STATE, {
  // getMyChallenges Legacy
  [Types.GET_MY_CHALLENGES]: getMyChallenges,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_MY_CHALLENGES_SUCCESS]: getMyChallengesSuccess,
  [Types.GET_MY_CHALLENGES_FAILURE]: getMyChallengesFailure,

  // getNewChallenges Legacy
  [Types.GET_NEW_CHALLENGES]: getNewChallenges,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_NEW_CHALLENGES_SUCCESS]: getNewChallengesSuccess,
  [Types.GET_NEW_CHALLENGES_FAILURE]: getNewChallengesFailure,

  // getCompletedChallenges Legacy
  [Types.GET_COMPLETED_CHALLENGES]: getCompletedChallenges,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_COMPLETED_CHALLENGES_SUCCESS]: getCompletedChallengesSuccess,
  [Types.GET_COMPLETED_CHALLENGES_FAILURE]: getCompletedChallengesFailure,

  // getChallenge Legacy
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_CHALLENGE]: getChallenge,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_CHALLENGE_SUCCESS]: getChallengeSuccess,
  [Types.GET_CHALLENGE_FAILURE]: getChallengeFailure,
  [Types.RESET_CHALLENGE]: resetChallenge,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.JOIN_INDIVIDUAL_CHALLENGE]: joinIndividualChallenge,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.JOIN_INDIVIDUAL_CHALLENGE_SUCCESS]: joinIndividualChallengeSuccess,
  [Types.JOIN_INDIVIDUAL_CHALLENGE_FAILURE]: joinIndividualChallengeFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.JOIN_TEAM_CHALLENGE]: joinTeamChallenge,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.JOIN_TEAM_CHALLENGE_SUCCESS]: joinTeamChallengeSuccess,
  [Types.JOIN_TEAM_CHALLENGE_FAILURE]: joinTeamChallengeFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_LEADERBOARD]: getLeaderboard,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_LEADERBOARD_SUCCESS]: getLeaderboardSuccess,
  [Types.GET_LEADERBOARD_FAILURE]: getLeaderboardFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_TEAM_PARTICIPANTS]: getTeamParticipants,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_TEAM_PARTICIPANTS_SUCCESS]: getTeamParticipantsSuccess,
  [Types.GET_TEAM_PARTICIPANTS_FAILURE]: getTeamParticipantsFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.LEAVE_SELECTED_CHALLENGE]: leaveSelectedChallenge,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.LEAVE_SELECTED_CHALLENGE_SUCCESS]: leaveSelectedChallengeSuccess,
  [Types.LEAVE_SELECTED_CHALLENGE_FAILURE]: leaveSelectedChallengeFailure,

  // getTrackers Legacy
  [Types.GET_TRACKERS]: getTrackers,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.GET_TRACKERS_SUCCESS]: getTrackersSuccess,
  [Types.GET_TRACKERS_FAILURE]: getTrackersFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.CONNECT_TRACKER]: connectTracker,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.DISCONNECT_TRACKER]: disconnectTracker,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.MODIFYING_TRACKERS]: modifyingTrackers,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.ADD_METRICS_TRACKER]: addMetricsTracker,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SYNC_ALL_SAMPLES_FOR_TRACKERS]: syncAllSamplesForTrackers,

  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_ANY_TRACKER_LAST_CONNECTED_TIME]: setAnyTrackerLastConnectedTime,
})
